import React, { useState, useEffect } from "react";
import { Form, Select, Typography, message } from "antd";
import { useTranslation } from "react-i18next";
import EmptyDamageClassForm from "../Surveys/EmptyDamageClassForm";
import PeopleDamageForm from "../Surveys/PeopleDamageForm";
import FunctionalityDamageForm from "./FunctionalityDamageForm";
import StructuralDamageForm from "./StructuralDamageForm";
import NotStructuralDamageForm from "./NotStructuralDamageForm";
import DamageChurchForm from "./DamageChurchForm";
import CollapsedStructureDamageForm from "./CollapsedStructureDamageForm";
import axios from "axios";

const { Option } = Select;
const { Title } = Typography;

// Pseudo damage category: records a DamageWholeStructure via the
// WHOLE_STRUCTURE discriminator instead of a TypeDamage value.
const COLLAPSED = "COLLAPSED";

// Reminder shown when the surveyor picks Functionality (wording provisional).
const FUNCTIONALITY_REMINDER =
  "Here the application allows to insert a specific type of functionality time by time "
  + "for each apartment of the building. Once completed one apartment, you can repeat the operation.";

const BuildingComponentDamageForm = props => {
  // Hooks must run unconditionally on every render — keep them at the top
  // even though we may short-circuit to DamageChurchForm below.
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const isEditMode = props.isEditMode || false;
  const [damageClass, setDamageClass] = useState();
  const [damageClasses, setDamageClasses] = useState([]);

  useEffect(() => {
    axios
      .get(process.env.REACT_APP_API_ENDPOINT + "/building/enums/damage-classes")
      .then(({ data }) => {
        setDamageClasses(data);
      })
      .catch(function (error) {
        console.log(error);
      });
  }, []);

  useEffect(() => {
    if (props.record.damage) {
      // A non-church whole-structure damage renders as the Collapsed structure category.
      const loadedClass = props.record.damage.damageDiscriminator === "WHOLE_STRUCTURE"
        ? COLLAPSED
        : props.record.damage.damageClass;
      form.setFieldsValue({
        damageClass: loadedClass,
      });
      setDamageClass(loadedClass);
    }
  }, [props.record.damage, form]);

  // When the parent is a non-ordinary church, building damage is recorded
  // per Italian Civil Protection mechanism (M01–M28) instead of the
  // ordinary AeDES damage classes. Route to DamageChurchForm — but be
  // careful not to swallow legacy DamageBuildingStructural / NotStructural /
  // Functionality damages: their typeId/damage_class fields would silently
  // disappear when re-saved through the church form (MapStruct merges into
  // the existing entity and drops unknown fields). So only use the church
  // form when the parent is a church AND:
  //   (a) the loaded damage already has a CHURCH/WHOLE_STRUCTURE discriminator
  //       (editing a previously-saved church damage), OR
  //   (b) we're creating a new damage (no existing entity to preserve),
  //       identified by id missing or sentinel -1.
  // On non-church buildings a WHOLE_STRUCTURE damage is the generic
  // "Collapsed structure" category handled below.
  const damage = props.record?.damage;
  const isChurchParent = props.parentEnterprise?.subtype === "CULTURAL_HERITAGE_CHURCH";
  const existingDiscriminator = damage?.damageDiscriminator;
  const isChurchDamage = existingDiscriminator === "CHURCH_STRUCTURE"
                      || existingDiscriminator === "WHOLE_STRUCTURE";
  const isNewDamage = !damage?.id || damage.id === -1;
  if (isChurchParent && (isChurchDamage || isNewDamage)) {
    return (
      <DamageChurchForm
        record={props.record}
        parentCallback={props.parentCallback}
        isEditMode={props.isEditMode}
      />
    );
  }

  const components = {
    'empty': EmptyDamageClassForm,
    'structural': StructuralDamageForm,
    'notstructural': NotStructuralDamageForm,
    'people': PeopleDamageForm,
    'functionality': FunctionalityDamageForm,
    'collapsed': CollapsedStructureDamageForm,
  };

  const handleChange = (value) => {
    if (!isEditMode) return;
    setDamageClass(value);
    if (value === "Functionality") {
      message.info(FUNCTIONALITY_REMINDER, 8);
    }
    const newDamage = { ...props.record };
    if (value === COLLAPSED) {
      // Overall assessment of the whole structure, not a per-element damage.
      newDamage.damage.damageClass = null;
      newDamage.damage.damageDiscriminator = "WHOLE_STRUCTURE";
    } else {
      newDamage.damage.damageClass = value;
      newDamage.damage.damageDiscriminator = null;
    }
    // No required field of its own; don't leak the parent's validity (the sub-form reports its own).
    newDamage.valid = true;
    props.parentCallback(newDamage);
  };

  const getComponents = () => {
    if (damageClass) {
      const name = damageClass.toLowerCase();
      const DetailsComponent = components[name] || EmptyDamageClassForm;
      return <DetailsComponent record={props.record} parentCallback={props.parentCallback} damageClass={props.record.damage.damageClass || damageClass} isEditMode={isEditMode} />
    }
  };

  return (
    <Form
      form={form}
      layout="horizontal"
      labelCol={{ span: 8 }}
      wrapperCol={{ span: 16 }}
      className="details_form"
      name="buildingDamageForm"
    >
      <Title className="details_form_header"
        title="Damages details"
        subTitle="Building"
      />
      <Form.Item
        label="Damage category"
        name="damageClass"
        tooltip="Damage category"
      >
        <Select onChange={handleChange} disabled={!isEditMode}>
          {damageClasses.map(name => (
            <Option key={name} value={name}>
              {t('damageEnums.damageClass.' + name, name)}
            </Option>
          ))}
          <Option key={COLLAPSED} value={COLLAPSED}>
            {t('damageEnums.damageClass.' + COLLAPSED, 'Collapsed structure')}
          </Option>
        </Select>
      </Form.Item>
      {getComponents()}
    </Form>
  );
};

export default BuildingComponentDamageForm;
